import { Link } from "react-router-dom";
import { ALPHA_AGENTS } from "@/data/agents";
import { COURSES } from "@/data/courses";

/** La competencia de un mentor, DERIVADA de su clave y no escrita otra vez aquí.
 *
 *  Max no es el mentor «de Literatura»: es el de **Comunicación**, y lo será de los
 *  veintiséis cursos de esa competencia y de los que vengan. `a.key` es el slug —lo que
 *  cruza con el backend y con `/cursos/:slug`—, así que esto busca en `COURSES` en vez de
 *  tener una segunda tabla de nombres que se pueda desincronizar.
 */
const deLaCompetencia = (a) => COURSES.find((c) => c.slug === a.key) || {};

/**
 * LA FILA BLANCA — mentores, quién está detrás y el profesor. Fase 3 de la portada.
 *
 * ES LA FILA DE DESCANSO que pide la regla de balance que aprobó Gabri el 19 de octubre de
 * 2026: un negro por fila y de lado alterno, y **una fila blanca cada dos**. Va entre la
 * fila del saber —negro a la derecha— y la del Mundo —negro a la izquierda—, y aquí no hay
 * ni un bloque negro a propósito. Si alguien mete uno, `prueba_portada.mjs` lo canta.
 *
 * DE DÓNDE SALE. Los mentores ocupaban una sección entera: avatar de 84 px, titular de 52 y
 * cinco tarjetas con párrafo, unos 640 px de alto, DESPUÉS de que el que llega ya haya hecho
 * una lección de verdad en el hero. Gabri, 19 de octubre de 2026, sobre los párrafos:
 * *«los párrafos de mentor ahora indican asignaturas. se van»*. Y tenía razón: decían la
 * materia, que es la palabra heredada que esta casa retiró de cara al alumno el 8 de octubre.
 *
 * LO QUE QUEDA ES LA CARA Y LA COMPETENCIA, que es lo único que hay que saber aquí: que hay
 * alguien detrás de cada una y que se puede ir a ver sus cursos. Lo demás —qué da cada uno,
 * con qué tono— está a un clic, en la página de su competencia, donde significa algo.
 *
 * Y DESDE EL 29 DE OCTUBRE DE 2026 LAS CARAS SON GRANDES Y CORREN. Eran seis círculos de 56 px
 * en una rejilla; ahora son seis tarjetas de 168 que se deslizan hacia donde mira el cursor.
 * Está en `CarruselDeMentores.jsx`, con el porqué de cada decisión.
 *
 * Y SÓCRATES NO ES UN ENLACE. Los cinco mentores llevan a su competencia; él no tiene una,
 * porque está en todas las lecciones. Un retrato que se puede pulsar y lleva al catálogo
 * entero sería la tercera puerta al mismo sitio que esta portada ya pagó una vez.
 */
export default function FilaBlanca() {
  return (
    /* TONO `ceniza` DESDE EL 19 DE OCTUBRE DE 2026, y no es una decision de color: al subir
       «Como se estudia aqui» a la fila 2, esta fila paso a ir detras de la del saber, que es
       `nacar`. Dos secciones seguidas del mismo tono no son dos secciones: la frontera entre
       ellas desaparece. La regla es de Gabri (9 oct 2026) y la vigila `prueba_portada.mjs`.
       Lo que NO cambia es el balance: aqui sigue sin haber un solo bloque negro. */
    <section className="py-10 lg:py-14" data-tono="ceniza" data-testid="landing-fila-blanca">
      {/* DOS FILAS Y NO DOS COLUMNAS, desde el 3 de noviembre de 2026. Esto era una rejilla de
          siete y cinco: los mentores a la izquierda y, apiladas en la columna estrecha de la
          derecha, el profesor y «Para tu clase», que son las dos tarjetas que le hablan a quien
          decide. Quedaban pequeñas y con la foto a 104 px.

          Ahora las dos ocupan la barra entera, una a cada lado y al doble de tamaño, y los
          mentores pasan debajo con todo el ancho para ellos. Siguen en la misma `<section>`:
          partirla en dos obligaría a darle un tono distinto a cada mitad y a recolocar la
          escalera entera de la portada, que es lo que vigila `prueba_portada.mjs`. */}
      <div className="max-w-[1320px] mx-auto px-6 grid gap-4 lg:gap-5 lg:grid-cols-2">

          {/* QUIÉN ESTÁ DETRÁS. No estaba en la portada: la foto vive en `/origen` y desde
              aquí no había ni una línea que llevara allí. Y es lo que de verdad contesta la
              pregunta que se hace una madre delante de una plataforma con IA: quién ha
              escrito esto. La foto está en `public/` —no en un servidor de otro— desde que
              se montó la página del Origen. */}
          <div className="rounded-ficha bg-white p-6 sm:p-8 flex gap-5 sm:gap-7 items-center"
               data-testid="landing-quien-esta-detras">
            <img
              src="/angel-gabriel.webp"
              alt="Ángel Gabriel, profesor y autor de las lecciones"
              loading="lazy"
              className="w-[150px] sm:w-[200px] h-[188px] sm:h-[250px] object-cover rounded-caja flex-shrink-0"
            />
            <div>
              <div className="font-mono-alpha text-[10px] uppercase tracking-[0.22em] text-alpha-red">
                Quién está detrás
              </div>
              <h2 className="font-serif-display text-[25px] leading-[1.1] mt-1.5">
                Lo escribe un profesor, no un departamento de marketing.
              </h2>
              {/* NI UNA CIFRA ESCRITA A MANO aquí: las dos grandes las pinta el hero con
                  `/api/cifras`, contadas sobre lo publicado. Es la misma regla que dejó la
                  portada sin sus «+3500 estudiantes». */}
              <p className="text-[13px] leading-snug text-alpha-black/55 mt-2">
                Las lecciones están escritas y revisadas una a una, con el currículum del BOE
                delante.
              </p>
              <Link
                to="/origen"
                data-testid="landing-a-origen"
                className="inline-flex items-center gap-2 mt-3 font-mono-alpha text-[10px] uppercase
                           tracking-[0.16em] border-b border-alpha-black/25 pb-0.5
                           hover:border-alpha-red hover:text-alpha-red transition-colors"
              >
                El origen <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>

          {/* PARA TU CLASE. El panel del profesor existe desde el 18 de octubre de 2026 y la
              portada no lo nombraba: el que firma un piloto no es el alumno. El enlace va al
              formulario de siempre con el motivo puesto —`?motivo=piloto`—, y no a un
              segundo formulario para centros: dos formularios que recogen lo mismo son dos
              bandejas de entrada que nadie mira a la vez. */}
          <div className="rounded-ficha bg-white p-6 sm:p-8 flex gap-5 sm:gap-7 items-center"
               data-testid="landing-para-tu-clase">
            {/* LA MISMA FORMA QUE LA DE ARRIBA. Comparte proporción con el retrato de al
                lado —4:5— a propósito: dos fotos con relaciones distintas se recortan
                distinto y la fila deja de leerse como una fila.

                LA FOTO CAMBIÓ EL 3 DE NOVIEMBRE DE 2026. Era una imagen antigua de dominio
                público y ahora es un aula de verdad, hecha por Gabri. Entró a 3024×4032 y 1,4
                MB; aquí va recortada a 4:5, a 800×1000 y 92 kB, que es lo que esta tarjeta
                pinta como mucho: en `public/` el fichero se lo baja todo el que entra. */}
            <img
              src="/aula.webp"
              alt="Un aula vacía con pupitres de pala y el ventanal al fondo"
              loading="lazy"
              className="w-[150px] sm:w-[200px] h-[188px] sm:h-[250px] object-cover rounded-caja flex-shrink-0"
            />
            <div>
            <div className="font-mono-alpha text-[10px] uppercase tracking-[0.22em] text-alpha-red">
              Para tu clase
            </div>
            <h2 className="font-serif-display text-[25px] leading-[1.1] mt-1.5">¿Eres profesor?</h2>
            {/* LA OFERTA, Y ES VERDAD: el piloto es de un trimestre y es gratis para el centro
                (decidido el 21 de octubre de 2026, en `docs/negocio/`). Va en rojo sobre rojo
                muy claro y no en rojo macizo: a un profesor que está decidiendo si te deja
                entrar en su clase, un cartel de rebajas le resta credibilidad justo donde hace
                falta tenerla. El borde fino es el mismo recurso que usa «Actúa en». */}
            <span className="rounded-full inline-flex items-center mt-2.5 px-3 py-1.5
                             bg-alpha-red/[0.07] border border-alpha-red/35 text-alpha-red
                             font-mono-alpha text-[9.5px] uppercase tracking-[0.14em]"
                  data-testid="landing-trimestre-gratis">
              Un trimestre gratuito
            </span>
            <p className="text-[13px] leading-snug text-alpha-black/55 mt-2.5">
              Crea tu clase, manda tareas y mira quién las ha hecho y dónde se ha atascado.
              Sin instalar nada.
            </p>
            <ul className="flex flex-wrap gap-2 mt-3.5 font-mono-alpha text-[9.5px] uppercase
                           tracking-[0.1em] text-alpha-black/50">
              <li className="rounded-caja border border-alpha-black/10 px-3 py-1.5">Panel del profesor</li>
              <li className="rounded-caja border border-alpha-black/10 px-3 py-1.5">Tareas</li>
              <li className="rounded-caja border border-alpha-black/10 px-3 py-1.5">Avisos</li>
            </ul>
            <Link
              to="/contacto?motivo=piloto"
              data-testid="landing-pedir-piloto"
              className="inline-flex items-center gap-2 mt-3.5 font-mono-alpha text-[10px] uppercase
                         tracking-[0.16em] border-b border-alpha-black/25 pb-0.5
                         hover:border-alpha-red hover:text-alpha-red transition-colors"
            >
              Pedir el piloto <span aria-hidden="true">→</span>
            </Link>
            </div>
          </div>
      </div>

    </section>
  );
}
